<template>
  <container-wrapper-full>
    <a-skeleton
      v-if="isLoading"
      active />

    <div v-else>
      <plan-heading-wrapper
        :id="String(plan.id)"
        :plan-name="plan.name || ''"
        :apartment-name="plan.apartment?.name"
        :apartment-id="plan.apartment?.id"
        :can-edit="canRenamePlan"
        @submit-name="changeName">
        <template #status>
          <crm-plan-info
            :is-merged="Boolean(plan.merged_from_plan_id)"
            :plan="plan"
            :tags-list="tagsList"
            :can-change-assignees="canChangeAssignees"
            :can-change-ninja="canChangeNinja"
            :can-change-reviewer="canChangeReviewer"
            :options-tags="false"
            :attach-tags-to-plan="attachTagsToPlan"
            :unreal-rtx-preset="plan?.plan?.options?.unreal_rtx_preset"
            route-for-crm-plan="/crm/plans/3d/"
            @deadline-changed="update"
            @change-strategic-data="update" />
          <a-tag
            v-if="plan.apartment?.is_paused"
            color="orange">
            Апартамент на паузе
          </a-tag>
        </template>

        <template #breadcrumbAside>
          <invalidation-info
            :invalidations="planInvalidations"
            variant="breadcrumb" />
        </template>

        <template #actions>
          <actions-buttons
            :crm-plan-id="plan.id"
            :plan-type="PlanType.Type3d"
            :plan-status-furniture="plan.plan.status.furniture"
            :plan-status-unreal="plan.plan.status.unreal"
            :plan-status-gltf="plan.plan.status.gltf"
            :plan-id="plan.plan.id"
            :show-buttons="canResetUnreal"
            :allowed-reset-steps="allowedResetSteps"
            :buttons="buttons"
            @button-click="onButtonClick" />
        </template>
      </plan-heading-wrapper>

      <plan-main
        :plan="plan"
        :plan-id="id"
        :plan-type="type"
        :service-plan-id="plan?.plan?.id"
        :compare-data="compareCarousel"
        :enable-image-upload="true"
        :load-data="loadData"
        @verification-started="handleVerificationStarted">
        <template #after-controls>
          <a-row
            :gutter="[16, 16]"
            :style="{ marginTop: '16px' }">
            <a-col
              v-if="id && canViewBranding"
              :span="24">
              <a-button
                :style="{ width: '100%' }"
                @click="isBrandingModalOpen = true">
                Брендинг планировки
                <a-tag
                  color="#45A5FF"
                  :style="{ marginLeft: '.5rem' }">
                  New
                </a-tag>
              </a-button>
            </a-col>

            <a-col
              v-if="isGuardsVisible"
              :span="24">
              <a-button
                :style="{ width: '100%' }"
                @click="isGuardsModalOpen = true">
                Проверки планировок
                <a-tag
                  color="#45A5FF"
                  :style="{ marginLeft: '.5rem' }">
                  New
                </a-tag>
              </a-button>
            </a-col>
          </a-row>
        </template>
      </plan-main>

      <a-modal
        v-model:open="isBrandingModalOpen"
        centered
        width="720px"
        :footer="null"
        title="Брендинг планировки">
        <plan-branding-section
          v-if="id && canViewBranding"
          :plan-id="Number(id)"
          :can-edit="true"
          :show-title="false" />
      </a-modal>

      <a-modal
        v-model:open="isGuardsModalOpen"
        centered
        width="720px"
        :footer="null"
        title="Проверки планировок">
        <plan-guard-bindings
          :owner-type="GuardOwnerType.CrmPlan"
          :owner-id="Number(id)" />
        <p class="plan-page-3d__guards-hint">
          Кроме привязанных здесь, к планировке применяются проверки её апартамента и заказчика-загрузчика.
        </p>
      </a-modal>

      <section
        v-if="adjustments?.length"
        style="margin: 1rem 0;">
        <a-divider>{{ getLocaleText('adjustments') }}</a-divider>

        <a-collapse
          allow-multiple
          style="width: 100%;">
          <a-collapse-panel
            v-for="(adjust, index) in adjustments"
            :key="adjust.id"
            :header="`Корректировка №${index + 1}`"
            style="width: 100%;">
            <div v-if="adjust?.description">
              <a-divider style="margin: 0">
                Описание
              </a-divider>
              <a-flex
                style="margin: 24px 0">
                <div
                  :style="{ display: 'block', wordBreak: 'break-all', overflow: 'auto' }"
                  :innerHTML="sanitizeHTML(parsedText(adjust.description))" />
              </a-flex>
            </div>

            <div v-if="adjust?.reference_paths?.length">
              <a-divider style="margin: 0">
                Прикрепленные файлы
              </a-divider>

              <image-viewer :images="adjust.reference_paths">
                <a-row :gutter="[16, 16]">
                  <a-col
                    v-for="(path, idx) in adjust.reference_paths"
                    :key="idx"
                    :span="4"
                    :style="{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '140px',
                      textAlign: 'center'
                    }">
                    <template v-if="isImage(path)">
                      <img
                        :src="path"
                        :alt="`Корректировка ${index + 1}`"
                        :style="{
                          maxHeight: '100%',
                          width: 'auto',
                          objectFit: 'contain'
                        }"
                        @click.stop>
                    </template>
                    <template v-else>
                      <a-button
                        type="primary"
                        :href="path"
                        download
                        target="_blank"
                        :style="{
                          width: '100%'
                        }"
                        @click.stop>
                        Скачать {{ idx + 1 }}й файл ({{ getFileExtension(path) }})
                      </a-button>
                    </template>
                  </a-col>
                </a-row>
              </image-viewer>
            </div>
          </a-collapse-panel>
        </a-collapse>
      </section>

      <section
        v-if="canViewPresets || canChangeEnvironment"
        :style="{ marginBottom: '1.25rem' }">
        <a-row :gutter="[16, 16]">
          <a-divider v-if="canViewPresets">
            Пресеты
          </a-divider>
          <a-select
            v-if="canViewPresets"
            v-model:value="presetId"
            :disabled="!canChangePreset"
            :style="{ width: '100%' }"
            show-search
            :filter-option="fuzzyMatchOption"
            @change="changePlan3dPreset">
            <a-select-option
              v-if="!plan.preset_id"
              selected="selected"
              value>
              Выберите пресет (По умолчанию: {{ defaultPreset?.name }})
            </a-select-option>
            <a-select-option
              v-for="(preset) in presetsList"
              :key="preset.id"
              :value="preset.id"
              :label="preset.name">
              {{ preset.name }}
            </a-select-option>
          </a-select>
          <a-divider v-if="isEnvironmentVisible">
            Окружение
            <span v-if="plan.unreal_environment_id === null">- {{ defaultUnrealEnvironment }}</span>
          </a-divider>

          <environment-select
            v-if="isEnvironmentVisible"
            v-model:value="unrealEnvironmentId"
            :disabled="!canChangeEnvironment"
            @update:value="changePlan3dUnrealEnvironment" />
        </a-row>
      </section>

      <section
        v-if="canPlaceFurniture"
        :style="{ marginBottom: '1.25rem' }">
        <a-row :gutter="[16, 16]">
          <a-divider>Расстановка мебели ({{ getStageStatusText(furnitureStatus) }})</a-divider>
          <a-col
            :md="16"
            :span="24">
            <a-button
              :style="{ width: '100%' }"
              :href="plan.links.furniture.editor || plan.links.neural.editor"
              target="_blank"
              rel="noopener noreferrer">
              Конструктор мебели
            </a-button>
          </a-col>
          <a-col
            :md="8"
            :span="24">
            <a-button
              :style="{ width: '100%' }"
              :disabled="isButtonDisabled"
              @click="placeFurniture">
              Расставить мебель
            </a-button>
          </a-col>
        </a-row>
      </section>

      <section
        v-if="canRenderGltf"
        :style="{ marginBottom: '1.25rem' }">
        <a-row :gutter="[16, 16]">
          <a-divider>Рендер GLTF ({{ getStageStatusText(gltfStatus) }})</a-divider>
          <a-col
            :md="24"
            :span="24">
            <a-button
              :style="{ width: '100%' }"
              :disabled="isButtonDisabled"
              @click="openGltfRenderModal">
              Рендер GLTF
            </a-button>
          </a-col>
        </a-row>
      </section>

      <section
        v-if="canRenderPreview || canRenderResult"
        :style="{ marginBottom: '1.25rem' }">
        <a-row :gutter="[16, 16]">
          <a-divider>Превью/Результат ({{ getStageStatusText(unrealStatus) }})</a-divider>
          <a-col
            :md="8"
            :span="24">
            <a-button
              v-if="canRenderPreview"
              :style="{ width: '100%' }"
              :disabled="isButtonDisabled"
              @click="generatePreview">
              Генерировать превью
            </a-button>
          </a-col>
          <a-col
            :md="8"
            :span="24">
            <a-select
              v-if="canRenderResult"
              :disabled="isButtonDisabled"
              placeholder="Генерировать результат (качество)"
              :style="{ width: '100%' }"
              @change="handleResultQualityChange">
              <a-select-option
                v-for="quality in availableResultQualities"
                :key="quality"
                :value="quality">
                {{ quality }}
              </a-select-option>
            </a-select>
          </a-col>
          <a-col
            :md="8"
            :span="24">
            <a-button
              v-if="canRenderResult"
              :style="{ width: '100%' }"
              @click="isModalVisible = true">
              Генерировать с новыми параметрами
            </a-button>
          </a-col>
        </a-row>
      </section>

      <section
        v-if="isWidgetLinkConstructorVisible"
        :style="{marginBottom: '1rem'}">
        <widget-link-constructor
          :plan-id="plan.id"
          :plan-variants="plan?.widget_variants ?? []"
          :plan-invalidations="planInvalidations"
          :url="url"
          cache-scope="crm"
          :ask-cache-before-open="true"
          query-plan="crmPlanId" />
      </section>

      <section
        v-if="unrealStatus === 2"
        :style="{ marginBottom: '1.25rem' }">
        <a-collapse :style="{ width: '100%' }">
          <a-collapse-panel
            key="1"
            header="Смотреть (без кэша)"
            :style="{ width: '100%' }">
            <template #extra>
              <a-space>
                <a-button
                  target="_blank"
                  :href="widgetUrlNoCache"
                  @click="(e) => e.stopPropagation()">
                  Смотреть без кэша
                </a-button>
                <a-button
                  v-if="canViewWidget"
                  target="_blank"
                  :href="widgetUrl"
                  @click="(e) => e.stopPropagation()">
                  Смотреть с кэшем
                </a-button>
              </a-space>
            </template>

            <iframe
              :key="resultVersion"
              title="plan-widget"
              :style="{ margin: 'auto', border: 'none' }"
              :src="widgetUrlNoCache"
              width="100%"
              height="800" />
          </a-collapse-panel>
        </a-collapse>
      </section>

      <a-divider type="horizontal" />

      <section v-if="plan.apartment?.id">
        <a-row :gutter="[16,16]">
          <a-col :span="24">
            <loaded-files-list
              :files="apartmentFiles"
              :files-sum="apartmentFiles?.length"
              :loading="isSourcesArchiveCreating"
              @download="downloadSources" />
          </a-col>
        </a-row>
      </section>

      <section>
        <plan-quality-log
          :crm-plan-id="plan.id"
          :type="PlanType.Type3d" />

        <a-row
          type="flex"
          align="top"
          :style="{ gap: '1rem' }">
          <plan-status-logs
            :id="plan.id"
            :type="PlanType.Type3d" />

          <plan-comments
            :id="plan.id"
            ref="planComments"
            :auth="authUser"
            :type="PlanType.Type3d"
            :is-verification-mode="isVerificationMode"
            :expected-verification-comments="expectedVerificationComments"
            :update-activity-logs="updateActivityLogs"
            @published-comment="update"
            @verification-completed="handleVerificationCompleted" />
        </a-row>
      </section>

      <plan-revisions
        v-if="isPlanRevisionsVisible && plan.links"
        :crm-plan-id="plan.id"
        :links="plan.links" />

      <plan-history
        ref="planHistoryRef"
        :crm-plan-id="plan.id"
        :plan-type="PlanActivityLogType.CrmPlan3d" />

      <change-ninja-modal
        :plan-id="plan?.id"
        action="crm/changeCrmPlanNinja3d"
        @change-ninja-success="updateActivityLogs" />

      <change-manager-modal
        :plan-id="plan?.id"
        action="crm/changeCrmPlanManager3d"
        @change-manager-success="updateActivityLogs" />

      <change-reviewer-modal
        :plan-id="plan?.id"
        action="crm/changeCrmPlanReviewer3d"
        @change-reviewer-success="update" />

      <change-cutter-modal
        :plan-id="plan?.id"
        action="crm/changeCrmPlanCutter3d"
        @change-cutter-success="updateActivityLogs" />
    </div>
    <start-generation-modal
      :plan="plan"
      :visible="isModalVisible"
      @close="isModalVisible = false" />
    <start-gltf-render-modal
      :plan="plan"
      :visible="isGltfModalVisible"
      @close="isGltfModalVisible = false"
      @success="update" />
  </container-wrapper-full>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import {
  computed, ref, watch,
} from 'vue';
import { message, Modal } from 'ant-design-vue';

import { api } from '@/api';
import { PlanType } from '@/enums/planType';
import { launchWithConfirm } from '@/helpers/launchWithConfirm';
import { StepStatus } from '@/enums/StepStatus';
import { getPlanActionsForUser, executeAction } from '@/composables/planActions';
import { getWidgetRoute } from '@/router/helpers';

import ContainerWrapperFull from '@/Components/Common/ContainerWrapperFull.vue';
import PlanHeadingWrapper from '@/Components/Service/Plans/PlanHeadingWrapper.vue';
import ChangeNinjaModal from '@/Components/CRM/Plans/Modals/ChangeNinjaModal.vue';
import ChangeCutterModal from '@/Components/CRM/Plans/Modals/ChangeCutterModal.vue';
import ChangeManagerModal from '@/Components/CRM/Plans/Modals/ChangeManagerModal.vue';
import ChangeReviewerModal from '@/Components/CRM/Plans/Modals/ChangeReviewerModal.vue';
import PlanStatusLogs from '@/Components/CRM/Plans/StatusLogs.vue';
import PlanComments from '@/Components/CRM/Plans/Comments.vue';
import PlanQualityLog from '@/Components/CRM/QualityReview/PlanQualityLog.vue';
import CrmPlanInfo from '@/Components/CRM/Plans/CrmPlanInfo.vue';
import PlanMain from '@/Components/CRM/Plans/PlanMain.vue';
import LoadedFilesList from '@/Components/CRM/Apartments/LoadedFilesList.vue';
import ActionsButtons from '@/Components/CRM/Plans/ActionsButtons.vue';
import PlanBrandingSection from '@/Components/CRM/Plans/PlanBrandingSection.vue';
import widgetLinkConstructor from '@/Components/Common/widgetLinkConstructor.vue';
import PlanGuardBindings from '@/Components/Common/PlanGuardBindings.vue';
import getFlag from '@/helpers/getFlag';
import { GuardOwnerType } from '@/enums/guard';
import { getFilesAsNameAndUrl } from '@/utils/getFilesAsNameAndUrl';
import { rotationModes } from '@/enums/rotationMode';
import EnvironmentSelect from '@/Components/CRM/Plans/EnvironmentSelect.vue';
import { getEnvironmentDisplayName } from '@/helpers/environmentDisplayName';
import { useCameraNavigationOptions } from '@/composables/useCameraNavigationOptions';
import ImageViewer from '@/Components/Common/ImageViewer.vue';
import { parsedText, sanitizeHTML } from '@/utils/htmlTextParser';
import localeStore from '@/store/locale';
import StartGenerationModal from '@/Pages/CRM/Plans/modals/StartGenerationModal.vue';
import StartGltfRenderModal from '@/Pages/CRM/Plans/modals/StartGltfRenderModal.vue';
import { crmPlan3dResultQuality } from '@/enums/crmPlan3dResultQuality';
import { fuzzyMatchOption } from '@/helpers/fuzzyMatchOption';
import { useCrmPlanState } from '@/composables/useCrmPlanState';
import { PlanActivityLogType } from '@/enums/planActivityLogType';
import PlanHistory from '@/Components/PlanHistory.vue';
import PlanRevisions from '@/Components/PlanRevisions.vue';
import InvalidationInfo from '@/Components/Common/InvalidationInfo.vue';

const getStageStatusText = (status) => {
  switch (status) {
    case 1:
      return 'В процессе';
    case 2:
      return 'Выполнено';
    case -1:
      return 'Ошибка';
    default:
      return 'Не выполнялось';
  }
};

const props = defineProps({
  id: {
    default: null,
    type: Number,
  },
});

const router = useRouter();
const route = useRoute();
const store = useStore();

const { getLocaleText } = localeStore();

const id = computed(() => Number(props.id || route.params.id));
const plan = computed(() => store.state.crm.currentPlan);
const planInvalidations = computed(() => plan.value?.invalidations || plan.value?.plan?.invalidations || []);

const canViewBranding = computed(() => store.getters['auth/hasPermission']('api.crm.plans.branding.show'));
const canRenamePlan = computed(() => store.getters['auth/hasPermission']('api.crm.plans.rename'));
const canRenderGltf = computed(() => store.getters['auth/hasPermission']('api.crm.plans.render_gltf'));
const canPlaceFurniture = computed(() => store.getters['auth/hasPermission']('api.crm.plans.run_furniture_placement'));
const canRenderPreview = computed(() => store.getters['auth/hasPermission']('api.crm.plans.get_3d_preview'));
const qualityPermissions = {
  Medium: 'api.crm.plans.get_3d_result',
  High: 'api.crm.plans.get_3d_high',
  UltraHigh: 'api.crm.plans.get_3d_ultra_high',
  Low: 'api.crm.plans.get_3d_low',
  Unlit: 'api.crm.plans.get_3d_unlit',
};
const availableResultQualities = computed(() => Object.keys(crmPlan3dResultQuality).filter((quality) => (
  store.getters['auth/hasPermission'](qualityPermissions[quality])
)));
const canRenderResult = computed(() => availableResultQualities.value.length > 0);
const canViewPresets = computed(() => store.getters['auth/hasPermission']('api.crm.preset.index'));
const canChangePreset = computed(() => store.getters['auth/hasPermission']('api.crm.plans.change_preset'));
const canChangeEnvironment = computed(() => store.getters['auth/hasPermission'](
  'api.crm.plans.set_unreal_environment',
));

const isGuardsVisible = getFlag('isGuardsVisible');
const isPlanRevisionsVisible = getFlag('isPlanRevisionsVisible');
const isBrandingModalOpen = ref(false);
const isGuardsModalOpen = ref(false);

const style = ref('');
const isEnvironmentVisible = computed(() => getFlag('isEnvironmentVisible')
  || canChangeEnvironment.value);
const isWidgetLinkConstructorVisible = getFlag('isWidgetLinkConstructorVisible');

const presetId = ref('');
const unrealEnvironmentId = ref('');
const selectedPreset = ref('');
const selectedUnrealEnvironment = ref('');
const cameraNavigation = ref('');
const planComments = ref(null);
const isVerificationMode = ref(false);

const planHistoryRef = ref(null);

const isModalVisible = ref(false);

const expectedVerificationComments = ref(0);

store.dispatch('substances/getPresetsList', { crm_plan_ids: [id.value] });
store.dispatch('substances/getUnrealEnvironment');

const presetsList = computed(() => store.state.substances.presetsList);

/**
     * NOTE: Делается не через импорт rotationModes из hart-estate-widget
     * для того, чтобы на странице не грузился весь виджет.
     *
     * TODO: Заменить, когда оптимизируем виджет и его экспорты
     */
const widgetUrl = computed(() => getWidgetRoute({
  crmPlanId: id.value,
  rotationMode: rotationModes.THREESIXTY,
  locale: 'ru',
}).toString());

const widgetUrlNoCache = computed(() => {
  const url = new URL(widgetUrl.value);
  url.searchParams.set('noCache', '1');
  return url.toString();
});

store.dispatch('crm/getTagsList');

const updateActivityLogs = () => {
  if (planHistoryRef.value) {
    planHistoryRef.value.loadActivityLogs();
  }
};

const verificationData = ref({
  expectedComments: 0,
  receivedComments: 0,
});

const handleVerificationStarted = (eventData) => {
  isVerificationMode.value = true;
  expectedVerificationComments.value = (eventData?.checklistsCount || 0) + (eventData?.hasCustomChecklist ? 1 : 0);
  updateActivityLogs();
};

const handleVerificationCompleted = () => {
  isVerificationMode.value = false;
  verificationData.value = { expectedComments: 0, receivedComments: 0 };
  updateActivityLogs();
};

const loadData = () => store.dispatch('crm/getCrmPlan3dById', id.value);

// Именно watch, а не watchEffect: watchEffect трекает все реактивные чтения внутри
// dispatch и перезапускается на каждое изменение currentPlan, дублируя запрос.
watch(
  [id, () => route.name],
  ([planId, routeName]) => {
    if (planId && routeName === 'crm-plans3d-show') {
      loadData();
    }
  },
  { immediate: true },
);

const update = () => {
  store.dispatch('crm/getCrmPlan3dByIdWithoutLoading', id.value);
  updateActivityLogs();
};

const furnitureStatus = computed(() => store.state.crm.currentPlan.plan?.status?.furniture);
const unrealStatus = computed(() => store.state.crm.currentPlan.plan?.status?.unreal);
const gltfStatus = computed(() => store.state.crm.currentPlan.plan?.status?.gltf);

const blockStatuses = [StepStatus.WAIT, StepStatus.PROCESS];

const isButtonDisabled = computed(() => {
  const { status } = store.state.crm.currentPlan.plan;
  return blockStatuses.includes(status?.furniture) || blockStatuses.includes(status?.unreal);
});

const resultVersion = ref(null);

const refreshPlanState = async (state, isCurrent) => {
  const { data } = await api.getCrmPlan3dById(id.value, { silent: true, timeout: 10000 });
  if (!isCurrent()) return;
  store.commit('crm/setCurrentPlan', data);
  resultVersion.value = state.result_version;
  updateActivityLogs();
};

useCrmPlanState(id, refreshPlanState, () => !store.state.crm.isCurrentPlanLoading);

const buttons = computed(() => getPlanActionsForUser(PlanType.Type3d, store.state.crm.currentPlan, store
  .state.auth.currentUser));

const confirmText = 'Отправить в обработку?';

const processResultGeneration = (response, statusType) => {
  if (!response) return;

  const currentPlan = response.data;
  currentPlan.plan.status[statusType] = StepStatus.PROCESS;
  store.commit('crm/setCurrentPlan', currentPlan);
  location.reload();
};

const generatePreview = () => {
  Modal.confirm({
    title: `${confirmText}`,
    cancelText: 'Нет',
    okText: 'Да',
    centered: true,
    onOk: () => launchWithConfirm(
      (force, choice) => api.getCrmPlan3dPreview(id.value, style.value === '' ? undefined : style.value, force, choice),
    ).then((response) => processResultGeneration(response, unrealStatus)),
  });
};

const generateResult = () => {
  Modal.confirm({
    title: `${confirmText}`,
    cancelText: 'Нет',
    okText: 'Да',
    centered: true,
    onOk: () => launchWithConfirm(
      (force, choice) => api.getCrmPlan3dResult(id.value, style.value === '' ? undefined : style.value, force, choice),
    ).then((response) => processResultGeneration(response, unrealStatus)),
  });
};

const handleResultQualityChange = (quality) => {
  const messageText = quality === 'Unlit' || quality === 'UltraHigh'
    ? `Вы выбрали высокое качество. Это может занять много времени. Генерировать результат в качестве ${quality}?`
    : `Генерировать результат в качестве ${quality}?`;
  Modal.confirm({
    title: messageText,
    cancelText: 'Нет',
    okText: 'Да',
    centered: true,
    onOk: () => {
      const resultMethods = {
        Medium: (force, choice) => api.getCrmPlan3dResult(id.value, presetId.value, force, choice),
        High: (force, choice) => api.getCrmPlan3dResultHigh(id.value, force, choice),
        UltraHigh: (force, choice) => api.getCrmPlan3dResultUltraHigh(id.value, force, choice),
        Low: (force, choice) => api.getCrmPlan3dResultLow(id.value, force, choice),
        Unlit: (force, choice) => api.getCrmPlan3dResultUnlit(id.value, force, choice),
      };

      if (!resultMethods[quality]) return undefined;

      return launchWithConfirm(resultMethods[quality])
        .then((response) => processResultGeneration(response, unrealStatus));
    },
  });
};

const changeCameraNavigationMode = (value) => {
  cameraNavigation.value = value;
  api.changeCameraNavigationMode(plan.value.plan.id, value)
    .then(() => {
      store.dispatch('crm/getCrmPlan3dById', plan.value.id);
    });
};

const placeFurniture = () => {
  Modal.confirm({
    title: `${confirmText}`,
    cancelText: 'Нет',
    okText: 'Да',
    centered: true,
    onOk: () => launchWithConfirm(
      (force) => api.getCrmPlanFurniturePlacement(id.value, presetId.value || undefined, force),
    ).then((response) => processResultGeneration(response, furnitureStatus)),
  });
};

const onButtonClick = (action) => {
  executeAction(action, id.value, PlanType.Type3d, update, router, plan.value?.status);
};

watch(
  [() => plan.value?.preset_id, () => plan.value?.unreal_environment_id],
  ([nextPresetId, nextEnvironmentId]) => {
    presetId.value = nextPresetId;
    unrealEnvironmentId.value = nextEnvironmentId;
  },
  { immediate: true },
);

const changePlan3dPreset = () => {
  api.changeCrmPlanPreset({ id: id.value, presetId: presetId.value })
    .then(() => {
      selectedPreset.value = presetsList.value.find((preset) => preset.id === presetId.value);
      message.success(`Пресет успешно изменен на ${selectedPreset.value.name}`);
    });
};

const unrealEnvironmentList = computed(() => store.state.substances.unrealEnvironment);

const changePlan3dUnrealEnvironment = () => {
  if (!unrealEnvironmentId.value) return;
  api.changeUnrealEnvironment({ id: id.value, unrealEnvironmentId: unrealEnvironmentId.value })
    .then(() => {
      selectedUnrealEnvironment.value = unrealEnvironmentList.value
        .find((unrealEnvironment) => unrealEnvironment.id === unrealEnvironmentId.value);
      message.success(`Окружение успешно изменено на ${getEnvironmentDisplayName(selectedUnrealEnvironment.value)}`);
    });
};

store.dispatch('crm/getAdjustmentsByCrmPlanId', { id: id.value });

const adjustments = computed(() => store.state.crm.adjustments.items);

const isImage = (url) => /\.(svg|webp|jpe?g|png)$/i.test(url);
const getFileExtension = (url) => {
  const matchResult = url.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
  return matchResult ? matchResult[1] : '';
};

const url = api.getWidgetLinkForCrmId(id.value);
store.dispatch('plans/fetchFloors', url);

const isLoading = computed(() => store.state.crm.isCurrentPlanLoading);
const tagsList = computed(() => store.state.crm.tagsList);
const authUser = computed(() => store.state.auth.currentUser);
const type = PlanType.Type3d;
const attachTagsToPlan = (tagsIds) => store.dispatch('crm/attachTagsToPlan', { id: id.value, tagsIds }).finally(update);
const changeName = (name) => store.dispatch('crm/changePlanName', { id: id.value, name }).finally(update);

const isGltfModalVisible = ref(false);

const openGltfRenderModal = async () => {
  try {
    await store.dispatch('crm/getCrmPlan3dByIdWithoutLoading', id.value);
  } catch (error) {
    message.error('Не удалось обновить данные планировки перед рендером GLTF');
  } finally {
    isGltfModalVisible.value = true;
  }
};

const canChangeAssignees = computed(() => store.getters['auth/hasPermission']('ui.action.pages_crm_plans_id_3d.1')
  || (store.getters['auth/hasPermission']('ui.action.pages_crm_plans_id_3d.4')
    && plan.value?.manager?.id === store.state.auth.currentUser.id));

const canChangeNinja = computed(() => (
  Number(plan.value?.status?.id ?? plan.value?.status) !== -1
  && (canChangeAssignees.value || store.getters['auth/hasPermission']('ui.action.pages_crm_plans_id_3d.8'))
));

const canChangeReviewer = computed(() => {
  if (Number(plan.value?.status?.id ?? plan.value?.status) === -1) return false;

  return store.getters['auth/hasPermission'](
    'ui.action.pages_crm_plans_id_3d.1',
  ) || store.getters['auth/hasPermission'](
    'ui.action.pages_crm_plans_id_3d.4',
  );
});

const canResetUnreal = computed(() => store.getters['auth/hasPermission']('api.plans.reset_to_error'));
const allowedResetSteps = ['furniture', 'unreal', 'gltf'];
const canViewWidget = computed(() => store.getters['auth/hasPermission']('api.crm.plans.widget'));

const isSourcesArchiveCreating = computed(() => store.state.crm.apartments.isSourcesArchiveCreating);
const downloadSources = () => store.dispatch('crm/apartments/downloadSourcesArchive', plan.value?.apartment?.id);
const apartmentFiles = computed(() => getFilesAsNameAndUrl(plan.value?.apartment?.files || []));
const defaultPreset = computed(() => store.state.substances.defaultPreset);
const defaultUnrealEnvironment = computed(() => store.state.substances.defaultUnrealEnvironment);

const {
  filteredOptions: filteredCameraNavigationOptions,
  isSelectVisible: isCameraNavigationSelectVisible,
} = useCameraNavigationOptions();

const compareCarousel = computed(() => ({
  originals: plan.value?.pictures || [],
  svgs: plan.value?.links?.svg_editor_images || [],
  topViews: plan.value?.links?.top_view_images || [],
}));

</script>

<style>
.plan-page-3d__guards-hint {
  margin: 12px 0 0;
  font-size: 12px;
  color: #8a8a8a;
}

.plan-main-image {
  width: calc(100% / 3);
}
@media (max-width: 767px) {
  .plan-main-image {
    width: 100%;
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.ant-modal {
  top: 0 !important;
  max-width: 100% !important;
}

</style>
